import type { EducationContent } from '/index';

export default {
  topParas: [
    <>
      This category covers emissions from your business’s <strong>consumption of electricity, heat, and steam</strong>,
      along with any <strong>energy attribute certificates (EACs)</strong> applicable to the locations included within
      the boundaries of this carbon review.
    </>,
  ],
  exampleParas: [
    <>
      Emissions in this category include, for example, those from{' '}
      <strong>electricity used at your offices or warehouses</strong> to power lighting, IT equipment, heating, and
      cooling systems during the reporting period.
    </>,
  ],
  included: {
    points: [
      {
        hasBullet: true,
        title: <strong>Location</strong>,
        paras: [
          {
            para: 'Select the location for which you are entering consumption data.',
          },
          {
            para: 'This must be one of the sites within the defined boundaries of this carbon review and a location for which you have already provided location details.',
          },
        ],
      },
      {
        hasBullet: true,
        title: <strong>Type of energy</strong>,
        paras: [
          {
            para: 'Depending on your local energy distribution, you can select from the following:',
          },
          {
            hasBullet: true,
            para: (
              <>
                <strong>Electricity:</strong> Emissions from the generation and delivery of electricity purchased by
                your business for use at the selected location.
              </>
            ),
          },
          {
            hasBullet: true,
            para: (
              <>
                <strong>Heat or steam:</strong> Emissions from the generation and delivery of heat purchased through a{' '}
                <strong>district heating system</strong>, used to warm buildings or support operational processes at the
                selected location. This is also referred to as <strong>district heating</strong>.
              </>
            ),
          },
        ],
      },
      {
        hasBullet: true,
        title: <strong>Grid consumption data</strong>,
        paras: [
          {
            hasBullet: true,
            para: (
              <>
                <strong>Activity data:</strong> The total kWh of electricity, heat, or steam consumed at the selected
                location. This may be derived from either a shared meter (in a multi-tenant building) or your own
                dedicated meter.
              </>
            ),
          },
          {
            hasBullet: true,
            para: (
              <>
                <strong>Supplier intensity data:</strong> Emissions data provided directly by your energy supplier,
                typically expressed as carbon intensity per unit (e.g., kg CO₂e or t CO₂e per unit of product or
                service).
              </>
            ),
          },
        ],
      },
      {
        hasBullet: true,
        title: (
          <>
            <strong>(Optional)</strong> On-site renewables
          </>
        ),
        paras: [
          {
            para: (
              <>
                Enter the amount of electricity (in kWh) generated from <strong>on-site renewable sources</strong> such
                as solar panels or wind turbines during the reporting period, which was consumed directly by your
                business at the selected location.
              </>
            ),
          },
        ],
      },
      {
        hasBullet: true,
        title: <strong>Energy attribute certificate(s)</strong>,
        paras: [
          {
            para: 'Include any certificates or agreements between your business and third parties that demonstrate renewable energy procurement, such as:',
          },
          {
            hasBullet: true,
            para: <strong>Power Purchase Agreements (PPAs)</strong>,
          },
          {
            hasBullet: true,
            para: <strong>Renewable Energy Guarantees of Origin (REGOs)</strong>,
          },
          {
            hasBullet: true,
            para: <strong>Guarantees of Origin (GOs)</strong>,
          },
          {
            hasBullet: true,
            para: 'Or any equivalent renewable energy certificates applicable to the selected location.',
          },
        ],
      },
    ],
  },
  notIncluded: {
    points: [
      {
        hasBullet: true,
        title: <strong>Direct fuel combustion (scope 1)</strong>,
        paras: [
          {
            para: 'Emissions from fuels burned on-site (e.g., gas boilers, diesel generators) are reported under scope 1 – fuels used on-site, not here.',
          },
        ],
      },
      {
        hasBullet: true,
        title: <strong>Electricity for leased or rented buildings (scope 3)</strong>,
        paras: [
          {
            para: 'If your landlord pays for electricity at a rented or leased building, report those emissions under scope 3 – upstream leased buildings, not here.',
          },
        ],
      },
      {
        hasBullet: true,
        title: <strong>Electric vehicle charging</strong>,
        paras: [
          {
            para: 'Electricity used to charge company EVs at public stations is reported under scope 2 – EV charging on the go, not here.',
          },
        ],
      },
    ],
  },
} satisfies EducationContent;
